import { ChevronsLeft, ChevronsRight, Tag as TagIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { useTags } from '../../common/admin-hooks.js';
import { TagChip } from '../../scenario/components/TagChip.js';
import { TagsManageDialog } from '../../scenario/components/TagsManageDialog.js';
import type { ScenarioRow } from '../../scenario/types.js';

/**
 * Left rail on the Presentation detail page. Mirrors the legacy
 * `wf-audit/.../presenationItem/...` left sidebar:
 *   - "Manage Tags" button at the top → opens `TagsManageDialog`,
 *     which lists every license tag with edit + delete affordances
 *     and a "New Tag" button. (Per-scenario tag attachment stays on
 *     the toolbar's tag dropdown; the per-tag remove on the Info
 *     card is for *un-attaching* a tag from this scenario without
 *     deleting it license-wide.)
 *   - Grouped scenario list. Cards under each tag header show name,
 *     folder, modified date; the currently-active scenario gets a
 *     yellow border. Clicking any card swaps the detail view via
 *     `onSelect`.
 *
 * Used by `PresentationDetailPage`; receives the same scenarios list
 * the page already fetches via `useScenariosList()`.
 */
export interface PresentationScenariosRailProps {
  scenarios: ReadonlyArray<ScenarioRow>;
  activeFusionId: string | null;
  onSelect: (fusionId: string) => void;
  /** Collapsed state — when true, render a thin sliver with just the
   *  expand button. The parent owns the state so it can be persisted
   *  across reloads + shared with other layout decisions on the page. */
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function PresentationScenariosRail({
  scenarios,
  activeFusionId,
  onSelect,
  collapsed,
  onToggleCollapse,
}: PresentationScenariosRailProps): JSX.Element {
  const { data: tagRows = [] } = useTags();
  const [tagsManageOpen, setTagsManageOpen] = useState(false);

  // Group scenarios by attached tag — multi-tag scenarios appear in
  // each group (legacy parity). Untagged trail in a "Not tagged"
  // bucket so the operator can still reach them.
  const groups = useMemo(() => {
    interface Group {
      key: string;
      tag: { id: number; title: string; color: string | null; icon: string | null } | null;
      items: ScenarioRow[];
    }
    const byTag = new Map<number, Group>();
    const untagged: Group = { key: '__untagged__', tag: null, items: [] };
    // Index of license tags so we can render the tag chip header
    // even when a scenario references the tag id without inlining
    // the title (some engine responses do).
    const tagIndex = new Map<
      number,
      { id: number; title: string; color: string | null; icon: string | null }
    >();
    for (const r of tagRows) {
      const id = typeof r.id === 'number' ? r.id : null;
      const title = typeof r.title === 'string' ? r.title : null;
      if (id === null || title === null) continue;
      tagIndex.set(id, {
        id,
        title,
        color: typeof r.color === 'string' ? r.color : null,
        icon: typeof r.icon === 'string' ? r.icon : null,
      });
    }
    for (const s of scenarios) {
      const tags = s.tags ?? [];
      if (tags.length === 0) {
        untagged.items.push(s);
        continue;
      }
      for (const t of tags) {
        const ref = tagIndex.get(t.id) ?? {
          id: t.id,
          title: t.title,
          color: t.color ?? null,
          icon: t.icon ?? null,
        };
        let g = byTag.get(t.id);
        if (!g) {
          g = { key: String(t.id), tag: ref, items: [] };
          byTag.set(t.id, g);
        }
        g.items.push(s);
      }
    }
    const sortedByTag = [...byTag.values()].sort((a, b) =>
      (a.tag?.title ?? '').localeCompare(b.tag?.title ?? ''),
    );
    const out: Group[] = sortedByTag;
    if (untagged.items.length > 0) out.push(untagged);
    return out;
  }, [scenarios, tagRows]);

  // Collapsed rail — show a thin sliver with just the expand chevron.
  // Matches the legacy `wf-audit/.../presenationItem` left sidebar
  // toggle: operators who use the presentation page primarily for the
  // KPI table + screenshot can hide the rail entirely; one click brings
  // it back. State lives in the parent (persisted to localStorage) so
  // it survives reloads and scenario-switches.
  if (collapsed) {
    return (
      <aside className="flex h-full w-10 min-w-10 flex-col items-center gap-2 overflow-hidden border-r border-foreground/10 bg-foreground/[0.02] py-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8 text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          aria-label="Expand scenarios rail"
          title="Expand scenarios rail"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </aside>
    );
  }

  return (
    <aside className="flex h-full w-[280px] min-w-[280px] flex-col gap-2 overflow-y-auto border-r border-foreground/10 bg-foreground/[0.02] p-3">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setTagsManageOpen(true)}
          className="flex-1 justify-start border-foreground/15 text-foreground/85 hover:border-foreground/40 hover:text-foreground"
        >
          <TagIcon className="mr-2 h-3.5 w-3.5" />
          Manage Tags
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8 shrink-0 text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          aria-label="Collapse scenarios rail"
          title="Collapse scenarios rail"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {groups.map((g) => (
          <section key={g.key} className="space-y-1">
            <header className="flex items-center gap-1.5 px-1">
              {g.tag ? (
                <TagChip tag={g.tag} size="sm" />
              ) : (
                <span className="text-xs font-semibold italic text-foreground/55">Not tagged</span>
              )}
              <span className="text-[11px] text-foreground/40">({g.items.length})</span>
            </header>
            <ul className="space-y-1">
              {g.items.map((s) => {
                const active = s.fusionId === activeFusionId;
                return (
                  <li key={`${g.key}-${s.fusionId}`}>
                    <button
                      type="button"
                      onClick={() => onSelect(s.fusionId)}
                      className={
                        'group flex w-full flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition-colors ' +
                        (active
                          ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                          : 'border-foreground/10 bg-foreground/[0.03] text-foreground/85 hover:border-foreground/30 hover:bg-foreground/[0.06]')
                      }
                    >
                      <span className="line-clamp-2 text-sm font-medium leading-snug">
                        {s.name}
                      </span>
                      {s.folderName ? (
                        <span className="line-clamp-1 text-[11px] text-foreground/50">
                          {s.folderName}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <TagsManageDialog open={tagsManageOpen} onOpenChange={setTagsManageOpen} />
    </aside>
  );
}
